import type { ModelUsage, ModelUsageRow } from "../../../../hooks/useAdminStatus";

// Display rows for the on-box models panel. Kept out of the component so the
// formatting - which carries the estimated-vs-measured distinction - is
// unit-testable without rendering.

export const KIND_TRANSCRIPTION = "transcription";
export const KIND_EMBEDDING = "embedding";

const KIND_LABEL: Readonly<Record<string, string>> = {
  [KIND_TRANSCRIPTION]: "Transcription",
  [KIND_EMBEDDING]: "Embeddings",
};

const KIND_ENGINE: Readonly<Record<string, string>> = {
  [KIND_TRANSCRIPTION]: "whisper.cpp",
  [KIND_EMBEDDING]: "llama.cpp",
};

// Known kinds render in this order; anything new sorts after them by name
// rather than vanishing.
const KIND_ORDER: ReadonlyArray<string> = [KIND_TRANSCRIPTION, KIND_EMBEDDING];

export type LocalModelRow = {
  readonly key: string;
  readonly kindLabel: string;
  readonly engine: string | null;
  readonly model: string;
  readonly runs: string;
  readonly workload: string;
  readonly workloadEstimated: boolean;
  readonly avgTime: string;
};

const formatCount = (n: number): string => Math.round(n).toLocaleString("en-US");

export const formatTokens = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return formatCount(n);
};

export const formatSeconds = (s: number): string => {
  if (s < 60) return `${Math.round(s)}s`;
  const mins = Math.floor(s / 60);
  if (mins < 60) return `${mins}m ${String(Math.round(s % 60)).padStart(2, "0")}s`;
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m`;
};

export const formatDurationMs = (ms: number): string => {
  if (ms < 1_000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1_000).toFixed(1)}s`;
  return formatSeconds(ms / 1_000);
};

// Transcription reports the audio it processed; embeddings report a token
// count we DERIVED from cache-row deltas. Never merge these into one number.
const workloadOf = (row: ModelUsageRow): { text: string; estimated: boolean } => {
  if (row.audioSeconds != null && row.audioSeconds > 0) {
    return { text: `${formatSeconds(row.audioSeconds)} of audio`, estimated: false };
  }
  if (row.inputTokens != null && row.inputTokens > 0) {
    return { text: `~${formatTokens(row.inputTokens)} tokens`, estimated: true };
  }
  return { text: "—", estimated: false };
};

const rank = (kind: string): number => {
  const i = KIND_ORDER.indexOf(kind);
  return i === -1 ? KIND_ORDER.length : i;
};

export const localModelRows = (usage: ModelUsage | null): ReadonlyArray<LocalModelRow> => {
  if (usage == null) return [];
  return [...usage.rows]
    .sort((a, b) => rank(a.kind) - rank(b.kind) || a.kind.localeCompare(b.kind) || a.model.localeCompare(b.model))
    .map((row) => {
      const workload = workloadOf(row);
      return {
        key: `${row.kind}:${row.model}`,
        kindLabel: KIND_LABEL[row.kind] ?? row.kind,
        engine: KIND_ENGINE[row.kind] ?? null,
        model: row.model,
        runs: row.invocations != null ? formatCount(row.invocations) : "—",
        workload: workload.text,
        // A row is only flagged estimated when the VIEW says so too - if the
        // backend ever starts measuring tokens, the caveat disappears here
        // without a frontend change.
        workloadEstimated: workload.estimated && usage.tokensEstimated,
        avgTime: row.avgDurationMs != null ? formatDurationMs(row.avgDurationMs) : "—",
      };
    });
};

export const hasEstimatedRow = (rows: ReadonlyArray<LocalModelRow>): boolean =>
  rows.some((r) => r.workloadEstimated);
